import React, { useState, useEffect, useRef } from 'react';
import { Layout, Row, Col, Grid, Tabs, Typography, Tag, Button, Modal, message, Segmented } from 'antd';
import { CarOutlined, ClockCircleOutlined, DashboardOutlined, DollarOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import VINSearch from './components/VINSearch/VINSearch';
import MarketAnalysis from './components/MarketAnalysis/MarketAnalysis';
import ActiveCompsContent from './components/ActiveCompsContent/ActiveCompsContent';
import SoldCompsContent from './components/SoldCompsContent/SoldCompsContent';
import { fetchVehicleHistoryUsage, generateVehicleHistoryReport } from './services/api';
import { fetchUserDetails } from '../Dashboard/services/api';

const { Content } = Layout;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;

const SearchInsights = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [vehicleData, setVehicleData] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState('Overview');
  const [sortBy, setSortBy] = useState('Price');
  const [sortDirection, setSortDirection] = useState('ASC');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [mileageRange, setMileageRange] = useState([20000, 80000]);
  const [isDragging, setIsDragging] = useState(null); // 'min' or 'max' or null
  const [isSimilarMileageActive, setIsSimilarMileageActive] = useState(false);
  const [isIndependentDealersOnly, setIsIndependentDealersOnly] = useState(false);
  
  // Sold tab states
  const [soldSortBy, setSoldSortBy] = useState('Price');
  const [soldSortDirection, setSoldSortDirection] = useState('ASC');
  const [showSoldSortDropdown, setShowSoldSortDropdown] = useState(false);
  const [soldMileageRange, setSoldMileageRange] = useState([20000, 80000]);
  const [isSoldDragging, setIsSoldDragging] = useState(null);
  const [isSoldSimilarMileageActive, setIsSoldSimilarMileageActive] = useState(false);
  const [isSoldIndependentDealersOnly, setIsSoldIndependentDealersOnly] = useState(false);
  const [showHistoryReportModal, setShowHistoryReportModal] = useState(false);
  const [historyUsageCount, setHistoryUsageCount] = useState(0);
  const [historyUsageLimit, setHistoryUsageLimit] = useState(5);
  const [historyRemaining, setHistoryRemaining] = useState(5);
  const [carfaxUrl, setCarfaxUrl] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [marketScope, setMarketScope] = useState('my-state');
  const [dealerState, setDealerState] = useState(null);
  const previousVinRef = useRef(null);
  const location = useLocation();

  // Fetch dealer state from user details
  useEffect(() => {
    const fetchDealerState = async () => {
      try {
        const userDetails = await fetchUserDetails();
        // The state comes from dealership_state in the API response
        const state = userDetails?.dealership_state || null;
        setDealerState(state);
      } catch (error) {
        console.error('Error fetching dealer state:', error);
      }
    };
    fetchDealerState();
  }, []);

  // Calculate min and max mileage from active listings
  useEffect(() => {
    if (vehicleData?.active_listings && vehicleData.active_listings.length > 0) {
      const mileageValues = vehicleData.active_listings
        .map(listing => listing.miles || 0)
        .filter(miles => miles > 0);
      
      if (mileageValues.length > 0) {
        const minMileage = Math.min(...mileageValues);
        const maxMileage = Math.max(...mileageValues);
        setMileageRange([minMileage, maxMileage]);
      }
    }
  }, [vehicleData?.active_listings]);

  // Calculate min and max mileage from recent sales
  useEffect(() => {
    if (vehicleData?.market_analysis?.recent_sales && vehicleData.market_analysis.recent_sales.length > 0) {
      const mileageValues = vehicleData.market_analysis.recent_sales
        .map(listing => listing.miles || 0)
        .filter(miles => miles > 0);
      
      if (mileageValues.length > 0) {
        const minMileage = Math.min(...mileageValues);
        const maxMileage = Math.max(...mileageValues);
        setSoldMileageRange([minMileage, maxMileage]);
      }
    }
  }, [vehicleData?.market_analysis?.recent_sales]);

  // Reset similar mileage and independent dealers states when tab changes (desktop)
  useEffect(() => {
    // Always reset all filter states when tab changes to ensure clean state
    setIsSimilarMileageActive(false);
    setIsSoldSimilarMileageActive(false);
    setIsIndependentDealersOnly(false);
    setIsSoldIndependentDealersOnly(false);
  }, [activeTab]);

  // Reset similar mileage and independent dealers states when mobile tab changes
  useEffect(() => {
    // Always reset all filter states when mobile tab changes to ensure clean state
    setIsSimilarMileageActive(false);
    setIsSoldSimilarMileageActive(false);
    setIsIndependentDealersOnly(false);
    setIsSoldIndependentDealersOnly(false);
  }, [activeMobileTab]);

  // Reset similar mileage and independent dealers states when new search is performed (backup)
  useEffect(() => {
    if (!vehicleData) return;
    
    // Reset all filter states when VIN changes (new search)
    const currentVin = vehicleData?.vehicle_info?.vin || vehicleData?.search_metadata?.vin;
    if (currentVin && currentVin !== previousVinRef.current) {
      // VIN changed - new search, reset states immediately
      setIsSimilarMileageActive(false);
      setIsSoldSimilarMileageActive(false);
      setIsIndependentDealersOnly(false);
      setIsSoldIndependentDealersOnly(false);
      previousVinRef.current = currentVin;
    }
  }, [
    vehicleData?.vehicle_info?.vin, 
    vehicleData?.search_metadata?.vin
  ]);

  // Add global mouse and touch event listeners for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleSliderDrag(isDragging, e);
      }
      if (isSoldDragging) {
        handleSoldSliderDrag(isSoldDragging, e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(null);
      }
      if (isSoldDragging) {
        setIsSoldDragging(null);
      }
    };

    const handleGlobalTouchMove = (e) => {
      if (isDragging) {
        e.preventDefault();
        const touch = e.touches[0];
        const fakeEvent = {
          clientX: touch.clientX,
          currentTarget: {
            parentElement: document.querySelector('[data-slider-container]')
          }
        };
        handleSliderDrag(isDragging, fakeEvent);
      }
      if (isSoldDragging) {
        e.preventDefault();
        const touch = e.touches[0];
        const fakeEvent = {
          clientX: touch.clientX,
          currentTarget: {
            parentElement: document.querySelector('[data-slider-container]')
          }
        };
        handleSoldSliderDrag(isSoldDragging, fakeEvent);
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDragging) {
        setIsDragging(null);
      }
      if (isSoldDragging) {
        setIsSoldDragging(null);
      }
    };

    if (isDragging || isSoldDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, isSoldDragging, mileageRange, soldMileageRange]);

  // Get actual min/max mileage from listings for slider bounds
  const getMileageBounds = () => {
    const listings = vehicleData?.active_listings || vehicleData?.market_analysis?.active_listings || [];
    if (!listings.length) return [0, 100000];
    
    const mileageValues = listings
      .map(listing => listing.miles || 0)
      .filter(miles => miles > 0);
    
    if (mileageValues.length === 0) return [0, 100000];
    
    return [Math.min(...mileageValues), Math.max(...mileageValues)];
  };

  // Calculate slider positions based on current range
  const getSliderPositions = () => {
    const bounds = getMileageBounds();
    const minBound = bounds[0];
    const maxBound = bounds[1];
    
    if (maxBound === minBound) return { leftPercent: 0, widthPercent: 100 };
    
    const leftPercent = ((mileageRange[0] - minBound) / (maxBound - minBound)) * 100;
    const rightPercent = ((mileageRange[1] - minBound) / (maxBound - minBound)) * 100;
    const widthPercent = rightPercent - leftPercent;
    
    return {
      leftPercent: Math.max(0, Math.min(100, leftPercent)),
      widthPercent: Math.max(0, Math.min(100, widthPercent))
    };
  };

  // Handle slider circle drag
  const handleSliderDrag = (type, event) => {
    const slider = event.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    const bounds = getMileageBounds();
    const minBound = bounds[0];
    const maxBound = bounds[1];
    const value = minBound + (percentage / 100) * (maxBound - minBound);
    
    if (type === 'min') {
      setMileageRange([Math.round(value), Math.max(mileageRange[1], Math.round(value))]);
    } else {
      setMileageRange([Math.min(mileageRange[0], Math.round(value)), Math.round(value)]);
    }
    
    // Reset similar mileage active state when user manually adjusts
    if (isSimilarMileageActive) {
      setIsSimilarMileageActive(false);
    }
  };

  // Sort and filter active listings
  const getSortedListings = () => {
    const listings = vehicleData?.active_listings || vehicleData?.market_analysis?.active_listings || [];
    
    if (!listings.length) return [];
    
    // Filter by mileage range; include listings with no mileage so they still show
    let filteredListings = listings.filter(listing => {
      const mileage = listing.miles;
      if (mileage == null) return true;
      return mileage >= mileageRange[0] && mileage <= mileageRange[1];
    });
    
    // Filter by independent dealers only if enabled
    if (isIndependentDealersOnly) {
      filteredListings = filteredListings.filter(listing => listing.dealer_type === 'independent');
    }
    
    // Sort the filtered listings
    const sortedListings = [...filteredListings].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'Price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'Mileage':
          aValue = a.miles || 0;
          bValue = b.miles || 0;
          break;
        default:
          aValue = a.price || 0;
          bValue = b.price || 0;
      }
      
      if (sortDirection === 'ASC') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return sortedListings;
  };

  // Calculate filtered averages
  const getFilteredAverages = () => {
    const filteredListings = getSortedListings();
    
    if (!filteredListings.length) return {
      avgMileage: 0,
      avgPrice: 0,
      activeCount: 0
    };
    
    // Filter out listings without valid prices for average price calculation
    const listingsWithPrice = filteredListings.filter(listing => listing.price && listing.price > 0);
    // Filter out listings without valid mileage for average mileage calculation
    const listingsWithMileage = filteredListings.filter(listing => listing.miles && listing.miles > 0);
    
    const totalMileage = listingsWithMileage.reduce((sum, listing) => sum + listing.miles, 0);
    const totalPrice = listingsWithPrice.reduce((sum, listing) => sum + listing.price, 0);
    
    return {
      avgMileage: listingsWithMileage.length > 0 ? Math.round(totalMileage / listingsWithMileage.length) : 0,
      avgPrice: listingsWithPrice.length > 0 ? Math.round(totalPrice / listingsWithPrice.length) : 0,
      activeCount: filteredListings.length
    };
  };

  // Sold tab functions
  const getSoldMileageBounds = () => {
    const listings = vehicleData?.market_analysis?.recent_sales || [];
    if (!listings.length) return [0, 100000];
    
    const mileageValues = listings
      .map(listing => listing.miles || 0)
      .filter(miles => miles > 0);
    
    if (mileageValues.length === 0) return [0, 100000];
    
    return [Math.min(...mileageValues), Math.max(...mileageValues)];
  };

  const getSoldSliderPositions = () => {
    const bounds = getSoldMileageBounds();
    const minBound = bounds[0];
    const maxBound = bounds[1];
    
    if (maxBound === minBound) return { leftPercent: 0, widthPercent: 100 };
    
    const leftPercent = ((soldMileageRange[0] - minBound) / (maxBound - minBound)) * 100;
    const rightPercent = ((soldMileageRange[1] - minBound) / (maxBound - minBound)) * 100;
    const widthPercent = rightPercent - leftPercent;
    
    return {
      leftPercent: Math.max(0, Math.min(100, leftPercent)),
      widthPercent: Math.max(0, Math.min(100, widthPercent))
    };
  };

  const handleSoldSliderDrag = (type, event) => {
    const slider = event.currentTarget.parentElement;
    const rect = slider.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    const bounds = getSoldMileageBounds();
    const minBound = bounds[0];
    const maxBound = bounds[1];
    const value = minBound + (percentage / 100) * (maxBound - minBound);
    
    if (type === 'min') {
      setSoldMileageRange([Math.round(value), Math.max(soldMileageRange[1], Math.round(value))]);
    } else {
      setSoldMileageRange([Math.min(soldMileageRange[0], Math.round(value)), Math.round(value)]);
    }
    
    // Reset similar mileage active state when user manually adjusts
    if (isSoldSimilarMileageActive) {
      setIsSoldSimilarMileageActive(false);
    }
  };

  const getSoldSortedListings = () => {
    const listings = vehicleData?.market_analysis?.recent_sales || [];
    
    if (!listings.length) {
      return [];
    }
    
    // Filter by mileage range; include listings with no mileage so they still show
    let filteredListings = listings.filter(listing => {
      const mileage = listing.miles;
      if (mileage == null) return true;
      return mileage >= soldMileageRange[0] && mileage <= soldMileageRange[1];
    });
    
    // Filter by independent dealers only if enabled
    if (isSoldIndependentDealersOnly) {
      filteredListings = filteredListings.filter(listing => listing.dealer_type === 'independent');
    }
    
    // Sort the filtered listings
    const sortedListings = [...filteredListings].sort((a, b) => {
      let aValue, bValue;
      
      switch (soldSortBy) {
        case 'Price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'Mileage':
          aValue = a.miles || 0;
          bValue = b.miles || 0;
          break;
        default:
          aValue = a.price || 0;
          bValue = b.price || 0;
      }
      
      if (soldSortDirection === 'ASC') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return sortedListings;
  };

  const getSoldFilteredAverages = () => {
    const filteredListings = getSoldSortedListings();
    
    if (!filteredListings.length) return {
      avgMileage: 0,
      avgPrice: 0,
      avgDOM: 0,
      soldCount: 0
    };
    
    // Filter out listings without valid prices for average price calculation
    const listingsWithPrice = filteredListings.filter(listing => listing.price && listing.price > 0);
    // Filter out listings without valid mileage for average mileage calculation
    const listingsWithMileage = filteredListings.filter(listing => listing.miles && listing.miles > 0);
    
    const totalMileage = listingsWithMileage.reduce((sum, listing) => sum + listing.miles, 0);
    const totalPrice = listingsWithPrice.reduce((sum, listing) => sum + listing.price, 0);
    const totalDOM = filteredListings.reduce((sum, listing) => sum + (listing.dom_active || 0), 0);
    
    return {
      avgMileage: listingsWithMileage.length > 0 ? Math.round(totalMileage / listingsWithMileage.length) : 0,
      avgPrice: listingsWithPrice.length > 0 ? Math.round(totalPrice / listingsWithPrice.length) : 0,
      avgDOM: totalDOM / filteredListings.length,
      soldCount: filteredListings.length
    };
  };

  // Clear data when navigating away from Search Insights page
  useEffect(() => {
    // Clear data when component unmounts (user navigates away)
    return () => {
      setVehicleData(null);
      localStorage.removeItem('hooptie_vehicle_data');
    };
  }, []);

  // Force re-render when route changes
  useEffect(() => {
    if (location.pathname === '/search') {
      // Reset state when navigating to search insights
      setVehicleData(null);
    }
  }, [location.pathname]);

  // Clear data when route changes (user navigates to different page)
  useEffect(() => {
    // Clear data when navigating away from Search Insights page
    // This includes going to dashboard, recommendations, profile, private-listings, etc.
    const searchInsightsRoutes = ['/search', '/insights/'];
    const isOnSearchInsightsRoute = searchInsightsRoutes.some(route => 
      location.pathname === route || location.pathname.startsWith(route)
    );
    
    if (!isOnSearchInsightsRoute) {
      setVehicleData(null);
      localStorage.removeItem('hooptie_vehicle_data');
    }
  }, [location.pathname]);

  // Fetch vehicle history usage count when component mounts or when vehicle is searched
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        // Get VIN from vehicleData if available
        const vin = vehicleData?.vehicle_info?.vin || vehicleData?.search_metadata?.vin || null;
        const response = await fetchVehicleHistoryUsage(vin);
        if (response?.data?.data) {
          // API returns { status: "success", data: { current_usage, limit, remaining, year, month, carfax_url } }
          const currentUsage = response.data.data.current_usage || 0;
          const limit = response.data.data.limit || 5;
          const remaining = response.data.data.remaining !== undefined 
            ? response.data.data.remaining 
            : Math.max(0, limit - currentUsage);
          
          setHistoryUsageCount(currentUsage);
          setHistoryUsageLimit(limit);
          setHistoryRemaining(remaining);
          setCarfaxUrl(response.data.data.carfax_url || null);
        }
      } catch (error) {
        console.error('Error fetching vehicle history usage:', error);
        // Keep default values on error
      }
    };
    fetchUsage();
  }, [vehicleData]);

  const handleVehicleDataReceived = (data) => {
    // Always reset all filter states immediately when new data is received
    // Use functional updates to ensure we're working with latest state
    setIsSimilarMileageActive(() => false);
    setIsSoldSimilarMileageActive(() => false);
    setIsIndependentDealersOnly(() => false);
    setIsSoldIndependentDealersOnly(() => false);
    
    // Update the previous VIN reference
    const currentVin = data?.vehicle_info?.vin || data?.search_metadata?.vin;
    if (currentVin) {
      previousVinRef.current = currentVin;
    }
    
    setVehicleData(data);
    
    // Force reset again after a microtask to ensure it sticks
    Promise.resolve().then(() => {
      setIsSimilarMileageActive(false);
      setIsSoldSimilarMileageActive(false);
      setIsIndependentDealersOnly(false);
      setIsSoldIndependentDealersOnly(false);
    });
  };

  return (
    <Content
      style={{
        padding: isMobile ? '16px 16px' : '16px 24px',
        marginTop: isMobile ? 20 : 8,
        backgroundColor: '#0d0f1a',
        minHeight: vehicleData ? '100vh' : 'auto',
        maxWidth: 'none',
        overflow: vehicleData ? 'auto' : 'hidden',
      }}
    >
      <VINSearch onDataReceived={handleVehicleDataReceived} />
      {vehicleData && (
        <div>
          {isMobile ? (
            // Mobile Layout
            <div>
              {/* Pricing Metrics */}

              {/* Price Information Line */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                marginBottom: 16,
              }}>
                {/* Hooptie Price */}
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: '#8ea0c6', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Hooptie Price
                  </div>
             <div style={{ color: '#10b981', fontSize: 16, fontWeight: 'bold' }}>
               {vehicleData?.pricing_info?.marketcheck_price || vehicleData?.pricing_info?.hooptie_price || vehicleData?.search_metadata?.asking_price ? 
                 `$${(vehicleData?.pricing_info?.marketcheck_price || vehicleData?.pricing_info?.hooptie_price || vehicleData?.search_metadata?.asking_price).toLocaleString()}` : 
                 'N/A'}
             </div>
                </div>
                
                {/* MSRP */}
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ color: '#8ea0c6', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    MSRP
                  </div>
                  <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 'bold' }}>
                    ${(vehicleData?.pricing_info?.msrp || 47000).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{
                height: 4,
                background: 'linear-gradient(90deg, #a855f7 0%, #3b82f6 50%, #22c55e 100%)',
                borderRadius: 2,
                marginBottom: 20,
              }}></div>

              {/* Mobile Tabs */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                padding: '16px 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                marginBottom: 20,
              }}>
                {['Overview', 'Active', 'Sold'].map((tab, index) => (
                  <button
                    key={tab}
                    onClick={() => setActiveMobileTab(tab)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: activeMobileTab === tab ? '#3b82f6' : '#8ea0c6',
                      fontSize: 16,
                      fontWeight: 500,
                      padding: '8px 16px',
                      borderBottom: activeMobileTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeMobileTab === 'Overview' && (
                <div>
                  {/* Vehicle Title */}
                  <div style={{ marginBottom: 16 }}>
                    <h1 style={{
                      color: '#ffffff',
                      fontSize: 24,
                      fontWeight: 700,
                      margin: '0 0 8px 0',
                      lineHeight: 1.2,
                    }}>
                      {vehicleData?.vehicle_info?.year} {vehicleData?.vehicle_info?.make} {vehicleData?.vehicle_info?.model}
                      {vehicleData?.vehicle_info?.version && (
                        <>
                          <span style={{ color: "#9EB0CB", fontWeight: 400, margin: "0 4px" }}>•</span>
                          <span style={{ color: "#9EB0CB", fontWeight: 400 }}>{vehicleData.vehicle_info.version}</span>
                        </>
                      )}
                    </h1>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}>
                      <div style={{
                        display: 'inline-block',
                        background: '#1e3a8a',
                        color: '#ffffff',
                        padding: '6px 12px',
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                      }}>
                        VIN: {vehicleData?.vehicle_info?.vin}
                      </div>
                      {vehicleData?.market_analysis?.recommendation?.recommendation && (() => {
                        const recommendation = vehicleData.market_analysis.recommendation.recommendation.toUpperCase();
                        let colors = {};
                        
                        if (recommendation === 'AVOID') {
                          colors = {
                            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#fca5a5'
                          };
                        } else if (recommendation === 'HOLD') {
                          colors = {
                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            color: '#fcd34d'
                          };
                        } else if (recommendation === 'BUY') {
                          colors = {
                            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            color: '#86efac'
                          };
                        } else {
                          // Default fallback
                          colors = {
                            background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.2) 0%, rgba(107, 114, 128, 0.1) 100%)',
                            border: '1px solid rgba(107, 114, 128, 0.3)',
                            color: '#d1d5db'
                          };
                        }
                        
                        return (
                          <div style={{
                            display: 'inline-block',
                            background: colors.background,
                            border: colors.border,
                            color: colors.color,
                            padding: '6px 12px',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}>
                            {vehicleData.market_analysis.recommendation.recommendation}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Price Range Cards */}
                  <div style={{ marginBottom: 24 }}>
                    {/* Sold Price Range */}
                    <div style={{
                      background: 'linear-gradient(135deg, #065f46, #047857)',
                      borderRadius: 12,
                      padding: 20,
                      marginBottom: 12,
                    }}>
                      <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                        Sold Price Range
                      </div>
                      <div style={{ color: '#10b981', fontSize: 20, fontWeight: 700 }}>
                        ${vehicleData?.market_analysis?.market_metrics?.price_range_sold?.min?.toLocaleString() || '31,961'} - ${vehicleData?.market_analysis?.market_metrics?.price_range_sold?.max?.toLocaleString() || '554,340'}
                      </div>
                    </div>

                    {/* Active Price Range */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1e3a8a, #1d4ed8)',
                      borderRadius: 12,
                      padding: 20,
                    }}>
                      <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                        Active Price Range
                      </div>
                      <div style={{ color: '#60a5fa', fontSize: 20, fontWeight: 700 }}>
                        ${vehicleData?.market_analysis?.market_metrics?.price_range_active?.min?.toLocaleString() || '33,988'} - ${vehicleData?.market_analysis?.market_metrics?.price_range_active?.max?.toLocaleString() || '555,445'}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Metrics */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}>
                    {/* Sold Listings */}
                    <div style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 12,
                      padding: 16,
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}>
                      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CarOutlined style={{ fontSize: 24, color: '#a855f7' }} />
                      </div>
                      <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {vehicleData?.market_analysis?.market_metrics?.sold_count || 3}
                      </div>
                      <div style={{ color: '#8ea0c6', fontSize: 12 }}>
                        Sold Listings
                      </div>
                    </div>

                    {/* Active Listings */}
                    <div style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 12,
                      padding: 16,
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}>
                      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CarOutlined style={{ fontSize: 24, color: '#3b82f6' }} />
                      </div>
                      <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {vehicleData?.market_analysis?.market_metrics?.active_count || 2}
                      </div>
                      <div style={{ color: '#8ea0c6', fontSize: 12 }}>
                        Active Listings
                      </div>
                    </div>

                    {/* Average DOM */}
                    <div style={{
                      flex: 1,
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 12,
                      padding: 16,
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}>
                      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ClockCircleOutlined style={{ fontSize: 24, color: '#3b82f6' }} />
                      </div>
                      <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {vehicleData?.market_analysis?.market_metrics?.avg_days_on_market != null ? Math.round(vehicleData.market_analysis.market_metrics.avg_days_on_market) : 362}
                      </div>
                      <div style={{ color: '#8ea0c6', fontSize: 12 }}>
                        Average DOM
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {activeMobileTab === 'Active' && (
                <div style={{ padding: '20px 0' }}>
                  {/* Vehicle Title */}
                  <div style={{ marginBottom: 16 }}>
                    <h1 style={{
                      color: '#ffffff',
                      fontSize: 24,
                      fontWeight: 700,
                      margin: '0 0 8px 0',
                      lineHeight: 1.2,
                    }}>
                      {vehicleData?.vehicle_info?.year} {vehicleData?.vehicle_info?.make} {vehicleData?.vehicle_info?.model}
                      {vehicleData?.vehicle_info?.version && (
                        <>
                          <span style={{ color: "#9EB0CB", fontWeight: 400, margin: "0 4px" }}>•</span>
                          <span style={{ color: "#9EB0CB", fontWeight: 400 }}>{vehicleData.vehicle_info.version}</span>
                        </>
                      )}
                    </h1>
                    
                    {/* Mileage */}
                    <div style={{
                      marginTop: 8,
                    }}>
                      <div style={{
                        color: '#60a5fa',
                        fontSize: 16,
                        fontWeight: 600,
                      }}>
                        {vehicleData?.search_metadata?.mileage?.toLocaleString() || 'N/A'} mi
                      </div>
                    </div>
                  </div>
                  
                  {/* Filter and Sort Section */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}>
                    {/* Sort by */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 16,
                      position: 'relative',
                      gap: 8,
                    }}>
                      <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                        Sort by
                      </div>
                      <div style={{ position: 'relative' }}>
                        <button 
                          onClick={() => setShowSortDropdown(!showSortDropdown)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 6,
                            color: '#ffffff',
                            padding: '8px 12px',
                            fontSize: 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            cursor: 'pointer',
                            minWidth: 80,
                          }}
                        >
                          {sortBy}
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        
                        {/* Dropdown Menu */}
                        {showSortDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: '#1a1f3a',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 6,
                            marginTop: 4,
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                          }}>
                            {['Price', 'Mileage'].map((option) => (
                              <button
                                key={option}
                                onClick={() => {
                                  setSortBy(option);
                                  setShowSortDropdown(false);
                                }}
                                style={{
                                  width: '100%',
                                  background: 'transparent',
                                  border: 'none',
                                  color: sortBy === option ? '#3b82f6' : '#ffffff',
                                  padding: '8px 12px',
                                  fontSize: 14,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                }}
                                onMouseEnter={(e) => {
                                  if (sortBy !== option) {
                                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (sortBy !== option) {
                                    e.target.style.background = 'transparent';
                                  }
                                }}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* ASC/DESC Arrow Buttons */}
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={() => setSortDirection('ASC')}
                          style={{
                            background: sortDirection === 'ASC' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 6,
                            color: '#ffffff',
                            padding: '8px 10px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 36,
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 3L9 9H3L6 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          onClick={() => setSortDirection('DESC')}
                          style={{
                            background: sortDirection === 'DESC' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 6,
                            color: '#ffffff',
                            padding: '8px 10px',
                            fontSize: 12,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 36,
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 9L3 3H9L6 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Mileage Section */}
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                      }}>
                        <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                          Mileage
                        </div>
                        <div style={{ color: '#ffffff', fontSize: 14 }}>
                          {mileageRange[0].toLocaleString()}–{mileageRange[1].toLocaleString()} mi
                        </div>
                      </div>
                      
                      {/* Mileage Slider */}
                      <div 
                        data-slider-container
                        style={{
                          height: 4,
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          position: 'relative',
                          marginBottom: 12,
                        }}
                      >
                        {/* Filled Section */}
                        <div style={{
                          position: 'absolute',
                          left: `${getSliderPositions().leftPercent}%`,
                          top: 0,
                          height: '100%',
                          width: `${getSliderPositions().widthPercent}%`,
                          background: '#60a5fa',
                          borderRadius: 2,
                        }}></div>
                        {/* Min Circle */}
                        <div 
                          style={{
                            position: 'absolute',
                            left: `${getSliderPositions().leftPercent}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 16,
                            height: 16,
                            background: '#ffffff',
                            borderRadius: '50%',
                            border: '2px solid #60a5fa',
                            cursor: 'pointer',
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => {
                            setIsDragging('min');
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            setIsDragging('min');
                            e.preventDefault();
                          }}
                        ></div>
                        {/* Max Circle */}
                        <div 
                          style={{
                            position: 'absolute',
                            left: `${getSliderPositions().leftPercent + getSliderPositions().widthPercent}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 16,
                            height: 16,
                            background: '#ffffff',
                            borderRadius: '50%',
                            border: '2px solid #60a5fa',
                            cursor: 'pointer',
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => {
                            setIsDragging('max');
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            setIsDragging('max');
                            e.preventDefault();
                          }}
                        ></div>
                      </div>

                      {/* Similar Mileage Button */}
                      <button 
                        onClick={() => {
                          if (isSimilarMileageActive) {
                            // Turn off - reset to full range
                            const bounds = getMileageBounds();
                            setMileageRange(bounds);
                            setIsSimilarMileageActive(false);
                          } else {
                            // Turn on - set to ±10% of search mileage
                            const searchMileage = vehicleData?.search_metadata?.mileage;
                            if (searchMileage) {
                              const margin = searchMileage * 0.1; // 10% margin
                              const newMin = Math.max(0, Math.round(searchMileage - margin));
                              const newMax = Math.round(searchMileage + margin);
                              setMileageRange([newMin, newMax]);
                              setIsSimilarMileageActive(true);
                            }
                          }
                        }}
                        style={{
                          background: isSimilarMileageActive 
                            ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                            : 'transparent',
                          border: '1px solid #a855f7',
                          borderRadius: 20,
                          color: '#ffffff',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: isSimilarMileageActive ? 600 : 500,
                          cursor: 'pointer',
                          width: '100%',
                          transition: 'all 0.3s ease',
                          boxShadow: isSimilarMileageActive 
                            ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                            : 'none',
                        }}
                      >
                        {isSimilarMileageActive ? '✓ Similar mileage ±10%' : 'Similar mileage ±10%'}
                      </button>
                      
                      {/* Independent Dealers Only Button */}
                      <button 
                        onClick={() => {
                          setIsIndependentDealersOnly(!isIndependentDealersOnly);
                        }}
                        style={{
                          background: isIndependentDealersOnly 
                            ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                            : 'transparent',
                          border: '1px solid #a855f7',
                          borderRadius: 20,
                          color: '#ffffff',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: isIndependentDealersOnly ? 600 : 500,
                          cursor: 'pointer',
                          width: '100%',
                          transition: 'all 0.3s ease',
                          boxShadow: isIndependentDealersOnly 
                            ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                            : 'none',
                          marginTop: 8,
                        }}
                      >
                        {isIndependentDealersOnly ? '✓ Independent Dealers Only' : 'Independent Dealers Only'}
                      </button>
                    </div>
                  </div>

                  {/* Summary Cards for Active Tab */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                    {/* Top Row - Active Listings and Avg Mileage */}
                    <div style={{
                      display: 'flex',
                      gap: 12,
                    }}>
                      {/* Active Listings */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 20,
                        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        transition: 'all 0.3s ease',
                        minHeight: 80,
                      }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        }}>
                          <CarOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#93c5fd', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Active<br/>Listings
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getFilteredAverages().activeCount}
                          </div>
                        </div>
                      </div>

                      {/* Avg Mileage */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 16,
                        boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        transition: 'all 0.3s ease',
                        minHeight: 70,
                      }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                        }}>
                          <DashboardOutlined style={{ fontSize: 20, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#fcd34d', fontSize: 10, fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Avg<br/>Mileage
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {Math.round(getFilteredAverages().avgMileage / 1000)}k
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Row - Avg Price (full width) - Enhanced styling */}
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                      borderRadius: 16,
                      padding: 20,
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                      boxShadow: '0 4px 20px rgba(34, 197, 94, 0.1)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      transition: 'all 0.3s ease',
                    }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                      }}>
                        <DollarOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ color: '#86efac', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          Avg<br/>Price
                        </div>
                        <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          ${getFilteredAverages().avgPrice.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Active Listings */}
                  <div style={{ marginTop: 24 }}>
                    {getSortedListings().length > 0 ? (
                      getSortedListings().slice(0, 10).map((listing, index) => (
                      <div
                        key={listing.vdp_url || `${listing.heading}-${listing.price}-${listing.miles}`}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                          borderRadius: 12,
                          padding: 16,
                          marginBottom: 12,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        onClick={() => {
                          if (listing.vdp_url) {
                            window.open(listing.vdp_url, '_blank');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          width: '100%',
                        }}>
                          {/* Vehicle Image */}
                          <div
                            style={{
                              width: 80,
                              height: 60,
                              borderRadius: 8,
                              overflow: 'hidden',
                              backgroundColor: '#1a1a1a',
                              position: 'relative',
                              flexShrink: 0,
                            }}
                          >
                            {(() => {
                              const imageUrl = listing.photo;
                              if (!imageUrl) {
                                return (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 10, textAlign: 'center', padding: 4 }}>
                                    Image not available
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <img
                                    src={imageUrl}
                                    alt={listing.heading}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <div style={{ position: 'absolute', inset: 0, display: 'none', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.5)', fontSize: 10, padding: 4 }}>
                                    Image not available
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Vehicle Details */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Year Make Model */}
                            <div style={{
                              color: '#ffffff',
                              fontSize: 16,
                              fontWeight: 600,
                              marginBottom: 4,
                              textTransform: 'uppercase',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}>
                              {listing.heading || 'Vehicle'}
                            </div>

                            {/* Price and Mileage */}
                            <div style={{
                              color: '#ffffff',
                              fontSize: 14,
                              marginBottom: 4,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 16,
                            }}>
                              <span style={{ fontWeight: 600 }}>
                                ${listing.price ? listing.price.toLocaleString() : 'N/A'}
                              </span>
                              <span style={{ color: '#60a5fa' }}>
                                {listing.miles ? listing.miles.toLocaleString() : 'N/A'} mi
                              </span>
                            </div>

                            {/* Dealership */}
                            <div style={{
                              color: '#a0a3b1',
                              fontSize: 12,
                            }}>
                              {listing.dealer_name || listing.dealer?.name || 'Dealership'}
                            </div>
                          </div>

                          {/* Arrow */}
                          <div style={{
                            color: '#ffffff',
                            fontSize: 18,
                            flexShrink: 0,
                          }}>
                            →
                          </div>
                        </div>
                      </div>
                    ))
                    ) : (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                        borderRadius: 12,
                        padding: 24,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center',
                      }}>
                        <div style={{ color: '#a0a3b1', fontSize: 14, marginBottom: 8 }}>
                          No active listings found
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Debug: {vehicleData ? `Has data, keys: ${Object.keys(vehicleData).join(', ')}` : 'No vehicleData'}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Active Listings (root): {vehicleData?.active_listings ? `${vehicleData.active_listings.length} items` : 'undefined'}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Active Listings (market_analysis): {vehicleData?.market_analysis?.active_listings ? `${vehicleData.market_analysis.active_listings.length} items` : 'undefined'}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}

              {activeMobileTab === 'Sold' && (
                <div style={{ padding: '20px 0' }}>
                  {/* Vehicle Title */}
                  <div style={{ marginBottom: 16 }}>
                    <h1 style={{
                      color: '#ffffff',
                      fontSize: 24,
                      fontWeight: 700,
                      margin: '0 0 8px 0',
                      lineHeight: 1.2,
                    }}>
                      {vehicleData?.vehicle_info?.year} {vehicleData?.vehicle_info?.make} {vehicleData?.vehicle_info?.model}
                      {vehicleData?.vehicle_info?.version && (
                        <>
                          <span style={{ color: "#9EB0CB", fontWeight: 400, margin: "0 4px" }}>•</span>
                          <span style={{ color: "#9EB0CB", fontWeight: 400 }}>{vehicleData.vehicle_info.version}</span>
                        </>
                      )}
                    </h1>
                    
                    {/* Mileage */}
                    <div style={{
                      marginTop: 8,
                    }}>
                      <div style={{
                        color: '#60a5fa',
                        fontSize: 16,
                        fontWeight: 600,
                      }}>
                        {vehicleData?.search_metadata?.mileage?.toLocaleString() || 'N/A'} mi
                      </div>
                    </div>
                  </div>
                  
                  {/* Filter and Sort Section for Sold Tab */}
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 12,
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}>
                    {/* Sort Section */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 20,
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}>
                        <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                          Sort by
                        </div>
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => setShowSoldSortDropdown(!showSoldSortDropdown)}
                            style={{
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 6,
                              color: '#ffffff',
                              padding: '8px 12px',
                              fontSize: 14,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              minWidth: 80,
                            }}
                          >
                            {soldSortBy}
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {showSoldSortDropdown && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: '#1a1f3a',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 6,
                              marginTop: 4,
                              zIndex: 1000,
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            }}>
                              {['Price', 'Mileage'].map((option) => (
                                <button
                                  key={option}
                                  onClick={() => {
                                    setSoldSortBy(option);
                                    setShowSoldSortDropdown(false);
                                  }}
                                  style={{
                                    width: '100%',
                                    background: 'transparent',
                                    border: 'none',
                                    color: soldSortBy === option ? '#3b82f6' : '#ffffff',
                                    padding: '8px 12px',
                                    fontSize: 14,
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (soldSortBy !== option) {
                                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (soldSortBy !== option) {
                                      e.target.style.background = 'transparent';
                                    }
                                  }}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* ASC/DESC Arrow Buttons */}
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            onClick={() => setSoldSortDirection('ASC')}
                            style={{
                              background: soldSortDirection === 'ASC' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 6,
                              color: '#ffffff',
                              padding: '8px 10px',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 36,
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M6 3L9 9H3L6 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setSoldSortDirection('DESC')}
                            style={{
                              background: soldSortDirection === 'DESC' ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: 6,
                              color: '#ffffff',
                              padding: '8px 10px',
                              fontSize: 12,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 36,
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M6 9L3 3H9L6 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mileage Section */}
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                      }}>
                        <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600 }}>
                          Mileage
                        </div>
                        <div style={{ color: '#ffffff', fontSize: 14 }}>
                          {soldMileageRange[0].toLocaleString()}–{soldMileageRange[1].toLocaleString()} mi
                        </div>
                      </div>
                      
                      {/* Mileage Slider */}
                      <div 
                        data-slider-container
                        style={{
                          height: 4,
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          position: 'relative',
                          marginBottom: 12,
                        }}
                      >
                        {/* Filled Section */}
                        <div style={{
                          position: 'absolute',
                          left: `${getSoldSliderPositions().leftPercent}%`,
                          top: 0,
                          height: '100%',
                          width: `${getSoldSliderPositions().widthPercent}%`,
                          background: '#60a5fa',
                          borderRadius: 2,
                        }}></div>
                        {/* Min Circle */}
                        <div 
                          style={{
                            position: 'absolute',
                            left: `${getSoldSliderPositions().leftPercent}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 16,
                            height: 16,
                            background: '#ffffff',
                            borderRadius: '50%',
                            border: '2px solid #60a5fa',
                            cursor: 'pointer',
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => {
                            setIsSoldDragging('min');
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            setIsSoldDragging('min');
                            e.preventDefault();
                          }}
                        ></div>
                        {/* Max Circle */}
                        <div 
                          style={{
                            position: 'absolute',
                            left: `${getSoldSliderPositions().leftPercent + getSoldSliderPositions().widthPercent}%`,
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 16,
                            height: 16,
                            background: '#ffffff',
                            borderRadius: '50%',
                            border: '2px solid #60a5fa',
                            cursor: 'pointer',
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => {
                            setIsSoldDragging('max');
                            e.preventDefault();
                          }}
                          onTouchStart={(e) => {
                            setIsSoldDragging('max');
                            e.preventDefault();
                          }}
                        ></div>
                      </div>

                      {/* Similar Mileage Button */}
                      <button 
                        onClick={() => {
                          if (isSoldSimilarMileageActive) {
                            // Turn off - reset to full range
                            const bounds = getSoldMileageBounds();
                            setSoldMileageRange(bounds);
                            setIsSoldSimilarMileageActive(false);
                          } else {
                            // Turn on - set to ±10% of search mileage
                            const searchMileage = vehicleData?.search_metadata?.mileage;
                            if (searchMileage) {
                              const margin = searchMileage * 0.1; // 10% margin
                              const newMin = Math.max(0, Math.round(searchMileage - margin));
                              const newMax = Math.round(searchMileage + margin);
                              setSoldMileageRange([newMin, newMax]);
                              setIsSoldSimilarMileageActive(true);
                            }
                          }
                        }}
                        style={{
                          background: isSoldSimilarMileageActive 
                            ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                            : 'transparent',
                          border: '1px solid #a855f7',
                          borderRadius: 20,
                          color: '#ffffff',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: isSoldSimilarMileageActive ? 600 : 500,
                          cursor: 'pointer',
                          width: '100%',
                          transition: 'all 0.3s ease',
                          boxShadow: isSoldSimilarMileageActive 
                            ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                            : 'none',
                        }}
                      >
                        {isSoldSimilarMileageActive ? '✓ Similar mileage ±10%' : 'Similar mileage ±10%'}
                      </button>
                      
                      {/* Independent Dealers Only Button */}
                      <button 
                        onClick={() => {
                          setIsSoldIndependentDealersOnly(!isSoldIndependentDealersOnly);
                        }}
                        style={{
                          background: isSoldIndependentDealersOnly 
                            ? 'linear-gradient(135deg, #a855f7, #7c3aed)' 
                            : 'transparent',
                          border: '1px solid #a855f7',
                          borderRadius: 20,
                          color: '#ffffff',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: isSoldIndependentDealersOnly ? 600 : 500,
                          cursor: 'pointer',
                          width: '100%',
                          transition: 'all 0.3s ease',
                          boxShadow: isSoldIndependentDealersOnly 
                            ? '0 4px 12px rgba(168, 85, 247, 0.3)' 
                            : 'none',
                          marginTop: 8,
                        }}
                      >
                        {isSoldIndependentDealersOnly ? '✓ Independent Dealers Only' : 'Independent Dealers Only'}
                      </button>
                    </div>
                  </div>

                  {/* Summary Cards for Sold Tab */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                    {/* First Row - Sold Listings & Avg Mileage */}
                    <div style={{
                      display: 'flex',
                      gap: 12,
                    }}>
                      {/* Sold Listings */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 20,
                        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        transition: 'all 0.3s ease',
                        minHeight: 80,
                      }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        }}>
                          <CarOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#93c5fd', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Sold<br/>Listings
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {getSoldFilteredAverages().soldCount}
                          </div>
                        </div>
                      </div>

                      {/* Avg Mileage */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 16,
                        boxShadow: '0 4px 20px rgba(245, 158, 11, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        transition: 'all 0.3s ease',
                        minHeight: 70,
                      }}>
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                        }}>
                          <DashboardOutlined style={{ fontSize: 20, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#fcd34d', fontSize: 10, fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Avg<br/>Mileage
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {Math.round(getSoldFilteredAverages().avgMileage / 1000)}k
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Second Row - Avg Price & Avg DOM */}
                    <div style={{
                      display: 'flex',
                      gap: 12,
                    }}>
                      {/* Avg Price */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 20,
                        boxShadow: '0 4px 20px rgba(34, 197, 94, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        transition: 'all 0.3s ease',
                        minHeight: 80,
                      }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                        }}>
                          <DollarOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#86efac', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Avg<br/>Price
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            ${getSoldFilteredAverages().avgPrice.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* Avg DOM */}
                      <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
                        borderRadius: 16,
                        padding: 20,
                        boxShadow: '0 4px 20px rgba(139, 92, 246, 0.1)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        transition: 'all 0.3s ease',
                        minHeight: 80,
                      }}>
                        <div style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                        }}>
                          <ClockCircleOutlined style={{ fontSize: 24, color: '#ffffff' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{ color: '#c4b5fd', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Avg<br/>DOM
                          </div>
                          <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {Math.round(getSoldFilteredAverages().avgDOM)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sold Listings */}
                  <div style={{ marginTop: 24 }}>
                    {getSoldSortedListings().length > 0 ? (
                      getSoldSortedListings().slice(0, 10).map((listing, index) => (
                      <div
                        key={listing.vdp_url || `${listing.heading}-${listing.price}-${listing.miles}`}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                          borderRadius: 12,
                          padding: 16,
                          marginBottom: 12,
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          overflow: 'hidden',
                        }}
                        onClick={() => {
                          if (listing.vdp_url) {
                            window.open(listing.vdp_url, '_blank');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          width: '100%',
                        }}>
                          {/* Vehicle Image */}
                          <div
                            style={{
                              width: 80,
                              height: 60,
                              borderRadius: 8,
                              overflow: 'hidden',
                              backgroundColor: '#1a1a1a',
                              position: 'relative',
                              flexShrink: 0,
                            }}
                          >
                            {(() => {
                              const imageUrl = listing.photo;
                              if (!imageUrl) {
                                return (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 10, textAlign: 'center', padding: 4 }}>
                                    Image not available
                                  </div>
                                );
                              }
                              return (
                                <>
                                  <img
                                    src={imageUrl}
                                    alt={listing.heading}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const fallback = e.target.nextElementSibling;
                                      if (fallback) fallback.style.display = 'flex';
                                    }}
                                  />
                                  <div style={{ position: 'absolute', inset: 0, display: 'none', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.5)', fontSize: 10, padding: 4 }}>
                                    Image not available
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {/* Vehicle Details */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Year Make Model */}
                            <div style={{
                              color: '#ffffff',
                              fontSize: 16,
                              fontWeight: 600,
                              marginBottom: 4,
                              textTransform: 'uppercase',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}>
                              {listing.heading || 'Vehicle'}
                            </div>

                            {/* Price and Mileage */}
                            <div style={{
                              color: '#ffffff',
                              fontSize: 14,
                              marginBottom: 4,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 16,
                            }}>
                              <span style={{ fontWeight: 600 }}>
                                ${listing.price ? listing.price.toLocaleString() : 'N/A'}
                              </span>
                              <span style={{ color: '#60a5fa' }}>
                                {listing.miles ? listing.miles.toLocaleString() : 'N/A'} mi
                              </span>
                            </div>

                            {/* Dealership and DOM */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}>
                              <div style={{
                                color: '#a0a3b1',
                                fontSize: 12,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                minWidth: 0,
                              }}>
                                {listing.dealer_name || listing.dealer?.name || 'Dealership'}
                              </div>
                              {listing.dom_active && (
                                <div style={{
                                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
                                  border: '1px solid rgba(139, 92, 246, 0.3)',
                                  borderRadius: 12,
                                  padding: '2px 8px',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: '#c4b5fd',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}>
                                  {listing.dom_active} days
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Arrow */}
                          <div style={{
                            color: '#ffffff',
                            fontSize: 18,
                            flexShrink: 0,
                          }}>
                            →
                          </div>
                        </div>
                      </div>
                    ))
                    ) : (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                        borderRadius: 12,
                        padding: 24,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center',
                      }}>
                        <div style={{ color: '#a0a3b1', fontSize: 14, marginBottom: 8 }}>
                          No sold listings found
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Debug: {vehicleData ? `Has data, keys: ${Object.keys(vehicleData).join(', ')}` : 'No vehicleData'}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Recent Sales: {vehicleData?.market_analysis?.recent_sales ? `${vehicleData.market_analysis.recent_sales.length} items` : 'undefined'}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Sold Mileage Range: {soldMileageRange[0]} - {soldMileageRange[1]}
                        </div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Filtered Results: {getSoldSortedListings().length} items
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>
          ) : (
            // Desktop Layout (existing)
            <div>
              {/* Vehicle Identification with Pricing Spectrum */}
              <div style={{
                padding: isMobile ? '16px 12px' : '16px 20px',
                marginBottom: isMobile ? 16 : 20,
                background: isMobile ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)' : 'transparent',
                borderRadius: isMobile ? 18 : 16,
                border: isMobile ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                backdropFilter: isMobile ? 'blur(10px)' : 'none',
                boxShadow: isMobile ? '0 8px 24px rgba(0, 0, 0, 0.2)' : 'none',
              }}>
                {/* Year Make Model Trim */}
                <Typography.Title level={2} style={{ 
                  color: '#ffffff', 
                  margin: '0 0 8px 0', 
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '28px'
                }}>
                  {vehicleData?.vehicle_info?.year || 2020} {vehicleData?.vehicle_info?.make || 'Chevrolet'} {vehicleData?.vehicle_info?.model || 'Silverado'} {vehicleData?.vehicle_info?.trim || 'LT Trail Boss'}
                  {vehicleData?.vehicle_info?.version && (
                    <>
                      <span style={{ color: "#9EB0CB", fontWeight: 400, margin: "0 4px" }}>•</span>
                      <span style={{ color: "#9EB0CB", fontWeight: 400 }}>{vehicleData.vehicle_info.version}</span>
                    </>
                  )}
                </Typography.Title>
                
                {/* Mileage and VIN */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 12
                }}>
                  <Typography.Text style={{ 
                    color: '#8ea0c6', 
                    fontSize: 16
                  }}>
                    {(vehicleData?.search_metadata?.mileage || vehicleData?.vehicle_info?.mileage || 45000).toLocaleString()} miles
                  </Typography.Text>
                  <span style={{ color: '#8ea0c6', fontSize: 16 }}>•</span>
                  <Tag 
                    style={{
                      background: 'rgba(30, 30, 30, 0.8)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#ffffff',
                      fontFamily: 'monospace',
                      fontSize: 14,
                      fontWeight: 'bold',
                      padding: '2px 6px'
                    }}
                  >
                    VIN: {vehicleData?.vehicle_info?.vin || 'WA1LGAFE9ED018928'}
                  </Tag>
                </div>
                
                {/* Pricing Spectrum Container */}
                <div style={{ position: 'relative', height: 100 }}>
                  
                  {/* Gradient Bar */}
                  <div style={{
                    position: 'absolute',
                    top: 40,
                    left: 0,
                    right: 0,
                    height: 12,
                    borderRadius: 6,
                    background: 'linear-gradient(90deg, #9333ea 0%, #3b82f6 50%, #22d3ee 100%)',
                  }}>
                    
                  </div>
                  
                  {/* Price Labels */}
                  <div style={{ position: 'absolute', bottom: -20, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    {/* Hooptie Price */}
                    <div style={{ textAlign: 'left', position: 'relative' }}>
                      <div style={{ 
                        color: '#ffffff', 
                        fontSize: 16, 
                        marginBottom: 4, 
                        fontWeight: 600
                      }}>
                        Hooptie Price
                      </div>
                      <div style={{ color: '#10b981', fontSize: 24, fontWeight: 'bold' }}>
                        {vehicleData?.pricing_info?.marketcheck_price || vehicleData?.pricing_info?.hooptie_price || vehicleData?.search_metadata?.asking_price ? 
                          `$${(vehicleData?.pricing_info?.marketcheck_price || vehicleData?.pricing_info?.hooptie_price || vehicleData?.search_metadata?.asking_price).toLocaleString()}` : 
                          'N/A'}
                      </div>
                    </div>
                    
                    {/* MSRP */}
                    <div style={{ textAlign: 'right' }}>
                      <Typography.Text style={{ color: '#8ea0c6', fontSize: 16, display: 'block', marginBottom: 4, fontWeight: 600 }}>
                        MSRP
                      </Typography.Text>
                      <Typography.Text style={{ color: '#ffffff', fontSize: 24, fontWeight: 'bold' }}>
                        ${(vehicleData?.pricing_info?.msrp || 47000).toLocaleString()}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              </div>

          <Tabs 
            activeKey={activeTab}
            onChange={setActiveTab}
            size={isMobile ? "default" : "large"}
            tabBarStyle={{
              borderBottom: 'none',
              marginBottom: isMobile ? 20 : 24,
              fontSize: isMobile ? 16 : 16,
              background: 'transparent',
              padding: isMobile ? '0 8px' : '0',
            }}
            style={{
              color: '#e4e6eb',
            }}
            className="search-insights-tabs"
            tabBarExtraContent={{
              right: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Typography.Text style={{ 
                    color: '#8ea0c6', 
                    fontSize: 12, 
                    fontWeight: 500,
                    whiteSpace: 'nowrap'
                  }}>
                    Market Scope:
                  </Typography.Text>
                  <Segmented
                    value={marketScope}
                    onChange={setMarketScope}
                    options={[
                      { label: dealerState ? `My State (${dealerState})` : 'My State', value: 'my-state' },
                      { label: 'All States', value: 'all-states' }
                    ]}
                    className="market-scope-segmented"
                  />
                </div>
              )
            }}
          >
            <TabPane tab="Overview" key="overview">
              <MarketAnalysis 
                key={`overview-${vehicleData?.vehicle_info?.vin || ''}-${marketScope}`}
                data={vehicleData}
                activeTab={activeTab}
                marketScope={marketScope}
                dealerState={dealerState}
                onViewListing={(listing) => {
                  // Handle view listing action
                }}
              />
            </TabPane>
            <TabPane tab="Active Comps" key="active-comps">
              <ActiveCompsContent 
                key={`active-comps-${activeTab}-${vehicleData?.vehicle_info?.vin || ''}-${marketScope}`}
                data={vehicleData}
                marketScope={marketScope}
                dealerState={dealerState}
              />
            </TabPane>
            <TabPane tab="Sold Comps" key="sold-comps">
              <SoldCompsContent 
                key={`sold-comps-${activeTab}-${vehicleData?.vehicle_info?.vin || ''}-${marketScope}`}
                data={vehicleData}
                marketScope={marketScope}
                dealerState={dealerState}
              />
            </TabPane>
          </Tabs>

          <style>{`
            .search-insights-tabs .ant-tabs-nav {
              margin-bottom: 0 !important;
            }
            
            .search-insights-tabs .ant-tabs-nav::before {
              border-bottom: 1px solid rgba(255,255,255,0.1) !important;
            }
            
            .search-insights-tabs .ant-tabs-tab {
              color: #8ea0c6 !important;
              font-weight: 500 !important;
              padding: ${isMobile ? '16px 20px' : '12px 16px'} !important;
              border: none !important;
              background: transparent !important;
              transition: all 0.3s ease !important;
              font-size: ${isMobile ? '16px' : '14px'} !important;
              border-radius: ${isMobile ? '12px 12px 0 0' : '8px 8px 0 0'} !important;
              margin-right: ${isMobile ? '8px' : '4px'} !important;
            }
            
            .search-insights-tabs .ant-tabs-tab:hover {
              color: #ffffff !important;
              background: ${isMobile ? 'rgba(255, 255, 255, 0.05)' : 'transparent'} !important;
            }
            
            .search-insights-tabs .ant-tabs-tab-active {
              color: #ffffff !important;
              font-weight: 600 !important;
              background: ${isMobile ? 'rgba(59, 130, 246, 0.1)' : 'transparent'} !important;
              border: ${isMobile ? '1px solid rgba(59, 130, 246, 0.2)' : 'none'} !important;
            }
            
            .search-insights-tabs .ant-tabs-ink-bar {
              background: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%) !important;
              height: ${isMobile ? '4px' : '3px'} !important;
              border-radius: ${isMobile ? '4px' : '2px'} !important;
              bottom: -1px !important;
            }
            
            .search-insights-tabs .ant-tabs-content-holder {
              padding-top: ${isMobile ? '20px' : '24px'} !important;
            }
            
            @media (max-width: 767px) {
              .search-insights-tabs .ant-tabs-nav-wrap {
                padding: 0 8px !important;
              }
              
              .search-insights-tabs .ant-tabs-tab {
                flex: 1 !important;
                text-align: center !important;
                margin-right: 4px !important;
              }
            }
            
            .market-scope-segmented {
              background: transparent !important;
              border: 1px solid rgba(255, 255, 255, 0.1) !important;
              border-radius: 8px !important;
              padding: 2px !important;
            }
            
            .market-scope-segmented .ant-segmented-item {
              background: transparent !important;
              color: #8ea0c6 !important;
              border: none !important;
              border-radius: 6px !important;
              font-size: 14px !important;
              font-weight: 500 !important;
              padding: 6px 16px !important;
              transition: all 0.2s ease !important;
            }
            
            .market-scope-segmented .ant-segmented-item-selected {
              background: #9146FF !important;
              color: #ffffff !important;
              box-shadow: 0 2px 8px rgba(145, 70, 255, 0.4) !important;
            }
            
            .market-scope-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) {
              background: rgba(255, 255, 255, 0.05) !important;
            }
          `}</style>
            </div>
          )}
        </div>
      )}
      <Modal
        open={showHistoryReportModal}
        onCancel={() => setShowHistoryReportModal(false)}
        onOk={async () => {
          const vin = vehicleData?.vehicle_info?.vin || vehicleData?.search_metadata?.vin;
          if (!vin) {
            message.error('VIN not found');
            return;
          }

          setIsGeneratingReport(true);
          try {
            const response = await generateVehicleHistoryReport(vin);
            message.success('Vehicle history report generated successfully');
            setShowHistoryReportModal(false);
            
            // Open Carfax URL from response if available
            if (response?.data?.carfax_url) {
              window.open(response.data.carfax_url, '_blank');
            } else if (response?.data?.data?.carfax_url) {
              window.open(response.data.data.carfax_url, '_blank');
            }
            
            // Refresh usage count after generating report
            try {
              const vin = vehicleData?.vehicle_info?.vin || vehicleData?.search_metadata?.vin || null;
              const usageResponse = await fetchVehicleHistoryUsage(vin);
              if (usageResponse?.data?.data) {
                const currentUsage = usageResponse.data.data.current_usage || 0;
                const limit = usageResponse.data.data.limit || 5;
                const remaining = usageResponse.data.data.remaining !== undefined 
                  ? usageResponse.data.data.remaining 
                  : Math.max(0, limit - currentUsage);
                
                setHistoryUsageCount(currentUsage);
                setHistoryUsageLimit(limit);
                setHistoryRemaining(remaining);
                setCarfaxUrl(usageResponse.data.data.carfax_url || null);
              }
            } catch (error) {
              console.error('Error refreshing usage count:', error);
            }
          } catch (error) {
            message.error(error.response?.data?.message || error.response?.data?.detail || 'Failed to generate vehicle history report');
          } finally {
            setIsGeneratingReport(false);
          }
        }}
        okText="Generate Report"
        cancelText="Cancel"
        title="Generate Vehicle History Report"
        centered
        bodyStyle={{
          padding: '24px',
          background: '#1A1A2E',
        }}
        styles={{
          content: {
            background: '#1A1A2E',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
          header: {
            background: '#1A1A2E',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '20px 24px',
          },
          footer: {
            background: '#1A1A2E',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '16px 24px',
          },
          title: {
            color: '#ffffff',
            fontSize: '22px',
            fontWeight: 'bold',
          }
        }}
        okButtonProps={{
          disabled: historyRemaining === 0 || isGeneratingReport,
          loading: isGeneratingReport,
          style: {
            background: historyRemaining === 0 || isGeneratingReport
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'linear-gradient(to right, #9333ea 0%, #3b82f6 100%)',
            border: 'none',
            color: historyRemaining === 0 || isGeneratingReport ? 'rgba(255, 255, 255, 0.3)' : '#ffffff',
            fontWeight: 600,
            borderRadius: 8,
            cursor: historyRemaining === 0 || isGeneratingReport ? 'not-allowed' : 'pointer',
          }
        }}
        cancelButtonProps={{
          style: {
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            borderRadius: 8,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Typography.Text style={{ color: '#ffffff', fontSize: 16, lineHeight: 1.6 }}>
            You're about to use one of your available Vehicle History Reports. Once generated, it will stay attached to this vehicle permanently.
          </Typography.Text>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 8,
            padding: '16px',
          }}>
            <Typography.Text style={{ color: '#8ea0c6', fontSize: 14 }}>
              Remaining usage after generating: <span style={{ color: '#ffffff', fontWeight: 600 }}>{Math.max(0, Math.max(0, historyUsageLimit - historyUsageCount) - 1)} reports</span>
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </Content>
  );
};

export default SearchInsights;
